/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 * 
 * Simplified panel height manager - manages app container height based on panel content
 */
import { ref, nextTick } from 'vue'

interface PanelHeightManager {
  updateAppHeight: (panelElement: HTMLElement | null, isOpen: boolean) => Promise<void>
  resetAppHeight: () => void
  currentAppHeight: Readonly<typeof appHeight>
  isScrolling: Readonly<typeof scrolling>
}

const appHeight = ref<string>('100vh')
const originalHeight = '100vh'
const scrolling = ref<boolean>(false)
let scrollTimeout: ReturnType<typeof setTimeout> | null = null

export function usePanelHeightManager(): PanelHeightManager {
  
  const updateAppHeight = async (panelElement: HTMLElement | null, isOpen: boolean): Promise<void> => {
    await nextTick()
    
    if (!isOpen || !panelElement) {
      resetAppHeight()
      return
    }

    if (document.body.classList.contains('panel-open')) {
      appHeight.value = '100vh'
      return
    }

    try {
      const panelContent = panelElement.querySelector('.panel-content') as HTMLElement
      if (!panelContent) {
        console.warn('Panel content element not found')
        return
      }

  
      const handleScroll = () => {
        scrolling.value = true
        if (scrollTimeout) clearTimeout(scrollTimeout)
        scrollTimeout = setTimeout(() => {
          scrolling.value = false
        }, 150)
      }

      // Add scroll listener
      panelContent.addEventListener('scroll', handleScroll, { passive: true })

      // Calculate height immediately but apply smoothly
      const calculateAndApplyHeight = () => {
        if (scrolling.value) {
          setTimeout(calculateAndApplyHeight, 100)
          return
        }

        const contentHeight = panelContent.scrollHeight
        const viewportHeight = window.innerHeight
        
        // Only update if panel content is taller than viewport
        if (contentHeight > viewportHeight) {
          const newHeight = Math.max(contentHeight + 100, viewportHeight)
          
          // Apply height smoothly
          if (appHeight.value !== `${newHeight}px`) {
            appHeight.value = `${newHeight}px`
          }
        } else {
          resetAppHeight()
        }
      }

      // Start height calculation immediately
      calculateAndApplyHeight()

      // Cleanup function for scroll listener
      const cleanup = () => {
        panelContent.removeEventListener('scroll', handleScroll)
        if (scrollTimeout) {
          clearTimeout(scrollTimeout)
          scrollTimeout = null
        }
      }

      // Store cleanup function on the element for later removal
      (panelElement as any).__heightManagerCleanup = cleanup

    } catch (error) {
      console.error('Error updating app height:', error)
      resetAppHeight()
    }
  }

  const resetAppHeight = (): void => {
    appHeight.value = originalHeight
    
    // Reset scrolling state
    scrolling.value = false
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
      scrollTimeout = null
    }
  }

  return {
    updateAppHeight,
    resetAppHeight,
    currentAppHeight: appHeight,
    isScrolling: scrolling
  }
} 